import React, { useState, useEffect, useRef } from 'react';
import { ticketsAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Headset, 
  Plus, 
  PaperPlaneTilt, 
  ChatCircleDots, 
  Clock, 
  CheckCircle,
  ArrowLeft,
  Spinner
} from '@phosphor-icons/react';

const statusColors = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const statusLabels = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const HelpPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
  });

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticketDetail?.messages]);

  const fetchTickets = async () => {
    try {
      const response = await ticketsAPI.getAll();
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const response = await ticketsAPI.getOne(ticketId);
      setTicketDetail(response.data);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      await ticketsAPI.create(newTicket);
      toast.success('Ticket created successfully!');
      setDialogOpen(false);
      setNewTicket({ subject: '', message: '' });
      fetchTickets();
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    setSending(true);
    try {
      await ticketsAPI.addMessage(selectedTicket, { message: newMessage });
      setNewMessage('');
      fetchTicketDetail(selectedTicket);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const openTicketChat = (ticketId) => {
    setSelectedTicket(ticketId);
    fetchTicketDetail(ticketId);
  };

  const closeTicketChat = () => {
    setSelectedTicket(null);
    setTicketDetail(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8 animate-spin text-[#FF5A36]" />
      </div>
    );
  }

  // Ticket Chat View
  if (selectedTicket && ticketDetail) {
    return (
      <div className="p-4 md:p-6 pb-24 md:pb-6">
        <Button 
          variant="ghost" 
          onClick={closeTicketChat}
          className="mb-4"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Tickets
        </Button>

        <Card className="border-[#E5E7EB]">
          <CardHeader className="border-b border-[#E5E7EB]">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-['Chivo'] text-xl">{ticketDetail.ticket.subject}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Created: {new Date(ticketDetail.ticket.created_at).toLocaleDateString('en-IN')}
                </p>
              </div>
              <Badge className={statusColors[ticketDetail.ticket.status]}>
                {statusLabels[ticketDetail.ticket.status]}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {/* Messages */}
            <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-gray-50">
              {ticketDetail.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender_type === 'designer' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      msg.sender_type === 'designer'
                        ? 'bg-[#FF5A36] text-white rounded-br-none'
                        : 'bg-white border border-gray-200 rounded-bl-none'
                    }`}
                  >
                    <p className={`text-xs font-medium mb-1 ${
                      msg.sender_type === 'designer' ? 'text-orange-100' : 'text-gray-500'
                    }`}>
                      {msg.sender_name}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    <p className={`text-xs mt-1 ${
                      msg.sender_type === 'designer' ? 'text-orange-200' : 'text-gray-400'
                    }`}>
                      {new Date(msg.created_at).toLocaleTimeString('en-IN', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Send Message */}
            {ticketDetail.ticket.status !== 'closed' && (
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1"
                  disabled={sending}
                />
                <Button 
                  type="submit" 
                  disabled={sending || !newMessage.trim()}
                  className="bg-[#FF5A36] hover:bg-[#E04828]"
                >
                  <PaperPlaneTilt className="h-5 w-5" weight="fill" />
                </Button>
              </form>
            )}

            {ticketDetail.ticket.status === 'closed' && (
              <div className="p-4 border-t border-gray-200 bg-gray-100 text-center text-gray-500">
                This ticket is closed
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tickets List View
  return (
    <div className="p-4 md:p-6 pb-24 md:pb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Chivo'] text-3xl font-black tracking-tighter text-[#121212]">Help & Support</h1>
          <p className="text-[#6B7280] mt-1">तुमच्या समस्या आम्हाला सांगा</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
              <Plus className="h-5 w-5 mr-2" weight="bold" />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Support Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  placeholder="Describe your problem in detail..."
                  rows={5}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full">
                Submit Ticket
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Help Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-6 text-center">
            <Clock className="h-10 w-10 text-blue-600 mx-auto mb-3" weight="duotone" />
            <h3 className="font-semibold text-gray-900">Response Time</h3>
            <p className="text-sm text-gray-600 mt-1">24 तासांच्या आत reply मिळेल</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-3" weight="duotone" />
            <h3 className="font-semibold text-gray-900">Expert Support</h3>
            <p className="text-sm text-gray-600 mt-1">Trained support team</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="pt-6 text-center">
            <ChatCircleDots className="h-10 w-10 text-purple-600 mx-auto mb-3" weight="duotone" />
            <h3 className="font-semibold text-gray-900">Live Chat</h3>
            <p className="text-sm text-gray-600 mt-1">Real-time conversation</p>
          </CardContent>
        </Card>
      </div>

      {/* Tickets List */}
      <Card className="border-[#E5E7EB]">
        <CardHeader>
          <CardTitle className="font-['Chivo'] flex items-center gap-2">
            <Headset className="h-6 w-6 text-[#FF5A36]" weight="duotone" />
            My Tickets
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <div className="text-center py-12">
              <Headset className="h-16 w-16 text-gray-300 mx-auto mb-4" weight="duotone" />
              <p className="text-gray-500">No tickets yet</p>
              <p className="text-sm text-gray-400 mt-1">Create a ticket if you need help</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={() => openTicketChat(ticket.id)}
                  className="p-4 border border-gray-200 rounded-lg hover:border-[#FF5A36] hover:bg-orange-50/50 cursor-pointer transition-all"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{ticket.subject}</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(ticket.updated_at).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <Badge className={statusColors[ticket.status]}>
                      {statusLabels[ticket.status]}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default HelpPage;
